import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import { Header } from '@app/navigation/header';
import { Body, Body1, Body7, Container, Page } from '@ui';
import { BackArrowButton } from '@ui/buttons';
import { ROUTES } from '@constants/routes';
import { AI_TOOLS } from '@constants/ai';
import { useCurrentUserToolsAvailable } from '@hooks/user/useCurrentUser';
// One import from the feature's barrel rather than fifteen deep paths: where these symbols live
// inside @features/feedView is the feature's business, not the page's.
import {
  buildColumnsParam,
  buildScopeParams,
  buildViewParams,
  canDrillDown,
  DrillDownUnavailableModal,
  BACK_CLEAR_LABEL,
  BACK_HOME_LABEL,
  FeedResultsPanel,
  FeedRunStatus,
  FeedViewFormPanel,
  READ_KINDS,
  RecentRuns,
  resolveMemberFeed,
  runKey,
  toFormPatch,
  toRecentRun,
  unrequestedPayloadColumns,
  useColumnNegotiation,
  useFeedNavigation,
  FeedViewContext,
  FeedViewProvider,
  useFeedReads,
  useFeedView,
  useMockFeedView,
  useRecentRuns,
} from '@features/feedView';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

// First-load form state, kept as a constant because back-to-start restores exactly this.
const INITIAL_FORM_STATE = {
  feedId: '',
  fromDate: null,
  toDate: null,
  companies: [],
  slim: false,
  useMock: false,
  mockKind: 'multi',
};

// One shared reference for "nothing is switched on and waiting", which is every read after the
// first.
const NO_AWAITED_COLUMNS = [];

const HEADING_ID = 'feed-view-heading';

// The page is a consumer, so the Provider has to sit outside it. Splitting here rather than in
// Router keeps "the Feed Viewer supplies its own query state" a fact about this page rather than
// about the route table.
export const FeedView = () => (
  <FeedViewProvider initialFormState={INITIAL_FORM_STATE}>
    <FeedViewPage />
  </FeedViewProvider>
);

const FeedViewPage = () => {
  const navigate = useNavigate();
  const tools = useCurrentUserToolsAvailable();

  const {
    isRunning,
    result,
    error,
    retryable,
    failure,
    isSuperseded,
    run,
    present,
    restore,
    reset,
    retry,
    lastRunFeedId,
    cancel,
  } = useFeedView();
  const runMock = useMockFeedView(present);
  const { runs: recentRuns, record: recordRun, remove: removeRecentRun, clear: clearRecentRuns } = useRecentRuns();

  const navigation = useFeedNavigation();
  const { stack, popTo, unavailable: unavailableDrillDown } = navigation;

  // Only what the page itself still needs. The form's ten props are FeedViewFormPanel's business
  // now, and it reads them from the same Context.
  const { formState, patchForm, resetForm, collapseForm, feeds, companyResolution } = React.useContext(FeedViewContext);

  // Folded once a result lands, so the table starts near the top rather than below the whole form.
  // Expanding is a deliberate act and sticks until the next result arrives.
  React.useEffect(() => {
    if (result) collapseForm();
  }, [result, collapseForm]);

  // The feed whose rows are actually being displayed. EVERYTHING the analyst can see or act on is
  // keyed on THIS — hidden columns, the chooser, the dropped-columns notice, the breadcrumb's head,
  // and the prompt panel. Submit feed B while A's table is up and B fails, and what stays on screen
  // is A, so all of those must still describe A.
  //
  // Read straight off the result, because the result carries it.
  const displayedFeedId = result?.feedId ?? null;

  const {
    isFixture,
    hiddenColumns,
    hiddenColumnsFor,
    toggleColumn,
    requestColumnsFor,
    knownPayloadFields,
    resolvePayloadFields,
    columnsRefused,
    refusedColumnNames,
    missingPayload,
    awaitedColumns,
    canLoadColumns,
    loadColumnsButtonLabel,
    resetColumns,
    forgetRefusal,
    claimAutoLoad,
    claimBootstrap,
    isRefused,
  } = useColumnNegotiation({ result, displayedFeedId, failure, lastRunFeedId });

  const { issueRead, issueFixture, abandonReads, keepPath, consumeClearPath, pendingRun } = useFeedReads({
    run,
    recordRun,
    result,
  });

  // The path a top-level read replaces, cleared when its ROWS arrive rather than when it is
  // submitted — see useFeedReads. Kept here rather than in the negotiation hook: which feed
  // is being shown is navigation, and 4.3 is where it goes.
  const filedResultRef = React.useRef(null);
  React.useEffect(() => {
    if (!result || result === filedResultRef.current) return;
    filedResultRef.current = result;
    if (consumeClearPath()) navigation.replacePath();
  }, [result, consumeClearPath, navigation]);

  // A table, a failed read, or one still loading all count as work on the page. Back deals with
  // that before it deals with leaving.
  const hasActiveFeed = !!result || isRunning || !!error;

  // Everything the page accumulates, back to first load: the result and any in-flight request (reset
  // aborts it), the drill-down path, the column choices, the folded form, and the query itself. The
  // query isn't lost — it's the top entry in Recent runs, which is history and deliberately survives.
  //
  // One thing deliberately survives too: columnsRefusedByFeed. A server that rejects the `columns`
  // key rejects it whatever this page has on screen, so forgetting the refusal here would only buy
  // the doubled round trip back on the next read.
  const clearPage = React.useCallback(() => {
    reset();
    navigation.reset();
    abandonReads();
    // reset() empties the result, and the derivation above reads the id off the result — so clearing
    // the pending id is all this has to do. Assigning `displayedRef` here as well would only race the
    // render that reset() is about to cause.
    resetColumns();
    // Start over is start over: the universe goes, so the automatic read has to be available again
    // or the feed would come back permanently without the columns it just forgot how to ask for.
    resetForm();
  }, [reset, navigation, abandonReads, resetColumns, resetForm]);

  // One control, two jobs, in escalating order: clear what's on the page, then leave. However deep
  // a drill-down went, one press clears all of it — stepping up one level is the breadcrumb's job,
  // and back doubling as that would leave no way to reach "start over".
  const handleBack = React.useCallback(() => {
    if (hasActiveFeed) {
      clearPage();
      return;
    }
    navigate(ROUTES.home);
  }, [hasActiveFeed, clearPage, navigate]);

  // Client-side hardening — the route is otherwise reachable by direct URL. The real gate is
  // server-side: a direct /feed/view caller must hold the feed_view grant.
  React.useEffect(() => {
    if (tools && tools.length > 0 && !tools.includes(AI_TOOLS.FEED_VIEW)) {
      navigate(ROUTES.home);
    }
  }, [tools, navigate]);

  // A submit is a fresh top-level read, so the drill-down path it replaces goes — but on arrival,
  // not on press. Until the new rows land the old ones are still what's on screen, and the path is
  // what says where they came from and how to get back.
  //
  // Resolves whatever is still in the company box FIRST, and uses what comes back. Text the analyst
  // pasted and did not press Add on would otherwise be dropped silently, and the run would come back
  // looking wrong for a reason nothing on screen explains.
  //
  // This is where the form's `onSubmit(override)` back-channel went. The form used to resolve and
  // then reach forward into this component's next-tick state to hand the result over; now the query
  // and the pending text have one owner, so the flush simply happens here and returns the list.
  // `flush()` is synchronous-ish when the box is empty — it returns the current companies — so the
  // common path still issues its read without waiting a render.
  //
  // THE COMMON PATH STAYS SYNCHRONOUS. Only a box with something still in it defers the run, and
  // only for as long as resolving that text takes. That was true before and is worth keeping: a
  // submit that always awaited would push every read a microtask out for the benefit of the rare
  // case, and the page's own tests are written against the press issuing the request.
  const issueSubmit = (state) => {
    if (state.useMock) {
      // A fixture is nobody's feed, so nothing it carries may be filed as a feed's universe — but the
      // analyst's column choices still have to land somewhere, and each kind is a different table.
      issueFixture();
      runMock({ slim: state.slim, kind: state.mockKind });
      return;
    }
    const params = buildViewParams(state);
    issueRead({
      kind: READ_KINDS.TOP_LEVEL,
      params,
      request: { ...params, ...requestColumnsFor(params.feedId, { slim: params.slim }) },
    });
  };

  // `override` is "run THIS query", for a caller that has already computed it. What it is no longer
  // is the form reaching forward into this component's next-tick state to deliver a company list it
  // resolved — that was the back-channel, and it is gone: the resolution lives beside the query now,
  // so the flush happens here and its result goes straight into the state being run.
  const handleSubmit = (override) => {
    const state = override ? { ...formState, ...override } : formState;
    if (!companyResolution.hasPendingText) {
      issueSubmit(state);
      return;
    }
    companyResolution.flush().then((companies) => issueSubmit({ ...state, companies }));
  };

  // Restores the run's window and company filter into the form, then reads it again — the point is
  // to repeat a query, not just to refill the feed field.
  const handleSelectRecentRun = (recent) => {
    const patch = toFormPatch(recent);
    patchForm(patch);
    // Folded on the PRESS, not on the arrival. A read is ~24 seconds and the running notice renders
    // below the form, so from an expanded form the only acknowledgement of a press was off-screen —
    // the press read as a press that did nothing. Batched with the patch above, so the summary that
    // replaces the form already states the run being made rather than the one before it.
    collapseForm();
    // Same rule as a submit: this is a top-level read, and the path it replaces survives until its
    // rows do.

    // Through the same builder a submit uses, against the form state this press is producing. It
    // used to hand-build the request field-for-field, and a second copy of it for the pending-run
    // ref — so a query control added to the form and the builder worked on Submit and silently did
    // nothing here, and the two paths drew different tables from an identically-labelled query.
    // This function is now structurally identical to handleSubmit.
    const params = buildViewParams({ ...formState, ...patch });
    issueRead({
      kind: READ_KINDS.TOP_LEVEL,
      params,
      request: { ...params, ...requestColumnsFor(params.feedId, { slim: params.slim }) },
    });
  };

  // Which listed run the read in flight is for, or null — so the row that was pressed can say so
  // for the whole ~24 seconds rather than only at the moment of the press.
  //
  // Read off the pending run, which is not new state: useFeedReads already keeps "the top-level query
  // in flight" there for the recording effect, and already clears it on every read that is NOT one
  // — a drill-down, a re-read for columns, a fixture. So a member feed's read marks no row for
  // free, which is right: it is navigation, not one of these queries.
  //
  // Identity is `runKey`, the same query identity the history dedupes on, so the marked row is the
  // one this read IS rather than the one at the index that was pressed. A submit typed into the
  // form that happens to reproduce a listed query therefore marks it too — also right, for the
  // same reason.
  //
  // Reading a ref in render is the pattern `displayedRef` above defends at length: the render that
  // reads it is the one `isRunning` just caused, and re-rendering reaches the same value.
  const runningRunKey =
    isRunning && pendingRun() ? runKey(toRecentRun({ params: pendingRun(), result: null, ranAt: null })) : null;

  // The way out of the negative cache, offered on the notice that reports it.
  //
  // A refusal is a fact about the server as it was deployed when that read went out, and servers get
  // redeployed — `extra: forbid` coming off the request model is a one-line change on the other side.
  // Without an escape, one 422 costs a feed its enrichment for the whole session, and clearPage
  // deliberately preserves the refusal, so even starting over doesn't undo it. This is the only thing
  // that does.
  //
  // The refusal is dropped and the read re-issued in the same press: clearing alone would leave the
  // analyst looking at a bare table and a notice, with the columns arriving only on some later read
  // they had no reason to make.
  const handleAskForColumnsAgain = () => {
    const feedId = displayedFeedId;
    // A fixture is nobody's feed and there is no server to ask again.
    if (!feedId || isFixture) return;

    forgetRefusal(feedId);

    // A re-read of the rows already on screen: not a query worth listing, and not a top-level read,
    // so the path standing over these rows stays exactly where it is.
    issueRead({
      kind: READ_KINDS.RE_READ,
      request: {
        ...buildScopeParams(formState),
        feedId,
        slim: !!formState.slim,
        ...requestColumnsFor(feedId, { ignoreRefusal: true, slim: !!formState.slim }),
      },
    });
  };

  // The button above the table. One press, one promise: bring back the columns these rows came
  // without.
  //
  // It exists because the alternative was a paragraph explaining additive `columns`, first reads and
  // spec-declared payload keys — an accurate account of a problem the analyst did not cause and
  // cannot act on except in one way. The one way is this.
  //
  // `dropSlim` is the difference between the two gaps it closes, and it is not a detail. A payload
  // column is EMPTIED by slim server-side, so a re-read that left it on would spend ~24 seconds and
  // come back identical — turning it off is part of keeping the promise, and the form state changes
  // with it so the checkbox visibly answers for what happened. An index flag is not a payload key
  // and survives slim untouched, so forcing it off there would take a choice made for speed away
  // from the analyst and buy nothing for it.
  const reReadForColumns = ({ dropSlim }) => {
    const feedId = displayedFeedId;
    // A fixture is nobody's feed and there is no server to ask.
    if (!feedId || isFixture) return;

    const slim = dropSlim ? false : !!formState.slim;
    if (dropSlim && formState.slim) patchForm({ slim: false });

    // A re-read of the rows already on screen: not a query worth listing, and not a top-level read,
    // so the path standing over these rows stays where it is.
    issueRead({
      kind: READ_KINDS.RE_READ,
      request: { ...buildScopeParams(formState), feedId, slim, ...requestColumnsFor(feedId, { slim }) },
    });
  };

  // What the one button actually presses. The payload gap sets the slim rule, because it is the gap
  // slim can hide: when both gaps are open the same re-read closes them together, and the label
  // above says so.
  const handleLoadColumns = () => reReadForColumns({ dropSlim: missingPayload });

  // Feeds this page has already spent a second read on. A ref because nothing renders from it, and
  // because the thing it must survive is a re-render rather than a re-mount: without it, cancelling
  // the automatic read would leave the page in exactly the state that triggers it and it would fire
  // again the moment the cancel settled.
  // What the automatic read WOULD ask for, against what the read on screen actually asked for.
  //
  // This comparison is the whole mechanism, and it is doing two jobs. First, ordering: on the commit
  // a response lands, the universe it teaches is still one setState away inside the filing effect
  // above, so a request built now is byte-identical to the one that just came back — the lists match,
  // nothing fires, and the effect waits for the commit where they differ. Nothing has to know which
  // effect runs first. Second, termination: once the automatic read has gone out, its own
  // `requestedColumns` names the flags, so the next comparison is equal whatever the server chose to
  // return — a feed that answers the request by ignoring it is asked exactly once.
  const requestableNow =
    !columnsRefused && !!displayedFeedId && !isFixture && awaitedColumns.length > 0
      ? requestColumnsFor(displayedFeedId, { slim: !!formState.slim }).columns || NO_AWAITED_COLUMNS
      : NO_AWAITED_COLUMNS;
  const alreadyRequested = result?.requestedColumns || NO_AWAITED_COLUMNS;
  const shouldAutoLoad = !isRunning && requestableNow.some((name) => !alreadyRequested.includes(name));

  // Read #1 of an unseen feed cannot name an index flag, so the Indices column and the index filter
  // were simply absent from it — and the fix was a press the analyst had to know to make. This makes
  // the second read automatic: the rows from read #1 are real and stay on screen while it runs (the
  // table dims itself through `isSuperseded`), so what this costs is the wait, not the result.
  //
  // `dropSlim: false`, unlike the button. Slim does not hide an index flag, and turning off a
  // score-only read the analyst chose for speed is not a decision to make on their behalf without a
  // press. The payload gap keeps its button for exactly that reason.
  //
  // Once per feed per page, on top of the comparison above. The comparison cannot see a CANCEL: the
  // read is aborted, no result lands, and every input to it is what it was a moment ago — so without
  // the ref, cancelling the automatic read starts the automatic read.
  React.useEffect(() => {
    if (!shouldAutoLoad) return;
    if (!claimAutoLoad(displayedFeedId)) return;
    reReadForColumns({ dropSlim: false });
    // reReadForColumns is rebuilt every render and reads the form as it is now; listing it would
    // re-run this on every keystroke, and the guards above are what decide when it may fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoLoad, displayedFeedId]);

  // Descend into a feed by id, whatever named it. Split out from handleDrillDown so the modal's
  // "try opening it anyway" enters by exactly the same door: an id the registry couldn't confirm is
  // still just an id, and it deserves the same capture/await/push treatment as a resolved one —
  // especially the deferred push, since an unconfirmed id is the likeliest of all to come back empty.
  //
  // THE PATH ENTRY IS PUSHED WHEN THE ROWS LAND, NOT WHEN THE PRESS HAPPENS, and everything else here
  // follows from that. Pushing first is what made a second press unsurvivable: until the first read
  // lands the table on screen is still the PARENT'S, so a second press captured the parent again and
  // pushed it under the first member's name. Its own read then superseded run 1, so run 1 returned no
  // verdict and never unwound, and when run 2 failed it popped to the depth it had captured — leaving
  // a breadcrumb reading "parent › member" over the parent's own rows. No unwinding could fix that,
  // because the entry was already wrong at the moment it was pushed. The old code answered by
  // dropping the second press entirely, which cost AC 15: drilling down during a read is supposed to
  // cancel that read, not be ignored by it.
  //
  // Deferring the push removes the cause. A second press supersedes the first read, run 1 comes back
  // with nothing, and there is no entry anywhere to unwind — so `popTo` and the depth it needed are
  // gone too, and with them the whole class of "unwound to a depth that has since moved" bug.
  //
  // The cost is a frame: between the member's rows arriving and this push, the table can render with
  // the trail one entry short. That understates the path for an instant; the old order overstated it,
  // and an understatement never names a feed above another feed's rows.
  const openMemberFeed = async (memberFeedId) => {
    // What WOULD be pushed if this read lands — the feed on SCREEN, which after a failed submit is
    // not the feed the form is pointed at. Captured before the request because that is the table the
    // press was made against; a press two seconds later against a different table is a different
    // parent, and reading it after the await would collapse the two.
    const parent = { feedId: displayedFeedId, result };

    // A2: the member's payload names, from its own spec, BEFORE the read rather than learned from it.
    // Skipped under slim, where the server empties the payload anyway and naming one is a 422 on the
    // whole read — see the note in buildColumnsParam.
    const slim = !!formState.slim;
    const known = slim ? null : knownPayloadFields(memberFeedId);
    const payloadFields = known === undefined ? await resolvePayloadFields(memberFeedId) : known;

    // NAVIGATION: not a query worth listing, and the path this descent is building must survive it —
    // including against a pending stack-clear left over from a submit that never landed.
    const response = await issueRead({
      kind: READ_KINDS.NAVIGATION,
      request: {
        ...buildScopeParams(formState),
        feedId: memberFeedId,
        slim,
        ...requestColumnsFor(memberFeedId, { slim, payloadFields }),
      },
    });

    // Null means only one thing — a NEWER run superseded this one — because useFeedView checks its
    // request-id guard before it looks at the abort flag, so a supersede never reaches the abort
    // branch. Whoever superseded it owns the screen, including the pending-feed ref, and touching
    // either from here would fight them for it.
    if (!response) return;

    // Rows never arrived: the read failed, or the analyst cancelled it with the Cancel button, which
    // comes back as the aborted response rather than as null precisely so it can be told from a
    // supersede. After a cancel NOBODY owns the screen, so this run has to put back what it moved.
    // Nothing was pushed, so all that moved is the pending-feed ref — and the rows still showing are
    // the parent's, so that is what it goes back to.
    //
    // The parent's result is deliberately not restored through the hook: it is what's already
    // showing, and restore() would additionally clear the error the analyst needs in order to know
    // the press went nowhere.
    if (!response.rows) {
      return;
    }

    // The rows are on screen, so the descent actually happened and there is now a table worth coming
    // back to. `push` appends to the CURRENT stack rather than to the one this render captured, which
    // is what keeps two overlapping presses from both claiming depth 0.
    navigation.descendedFrom(parent);

    // A1: the fallback for when the spec could not answer — a free-form `feed_name`, a spec fetch
    // that failed, or a feed whose universe turns out to carry payload columns its spec does not
    // declare. The response itself names them, so read the member once more, now knowing what to ask
    // for. This is the read the analyst can never issue by hand: Submit re-reads the parent, and
    // stepping back up restores the parent's rows rather than re-fetching anything.
    //
    // ONCE per feed per session, tracked on a ref rather than derived from the result, because the
    // thing that must not repeat is the ATTEMPT: a server that publishes a payload column and then
    // does not send it would otherwise re-read forever, at ~24 seconds a go.
    if (slim) return;
    if (isRefused(memberFeedId)) return;
    if (unrequestedPayloadColumns(response).length === 0) return;
    if (!claimBootstrap(memberFeedId)) return;

    // Built from the RESPONSE's own universe, not from availableColumnsByFeed. The filing effect
    // schedules that write and it is not readable in this tick — the same reason `payloadFields` and
    // `ignoreRefusal` are arguments rather than lookups.
    issueRead({
      kind: READ_KINDS.NAVIGATION,
      request: {
        ...buildScopeParams(formState),
        feedId: memberFeedId,
        slim: !!formState.slim,
        columns: buildColumnsParam({
          availableColumns: response.availableColumns,
          hiddenKeys: hiddenColumnsFor(memberFeedId),
          indexFields: response.indexFields,
        }),
      },
    });
  };

  // Open a member's own feed. The window and company filter carry over — the analyst is looking at
  // the same slice of the world, one level down. The column choices do not: they belong to the feed
  // they were made on, and the member's universe is its own.
  const handleDrillDown = (input) => {
    const { feedId: memberFeedId, key, reason, candidates } = resolveMemberFeed(input, feeds);
    // Unresolvable: explain it rather than swallowing the press. Requesting a guessed id would
    // either 422 or, worse, quietly show a different version's numbers under this heading.
    if (!memberFeedId) {
      navigation.markUnavailable({ memberKey: key, memberLabel: input?.label || null, reason, candidates });
      return Promise.resolve();
    }

    return openMemberFeed(memberFeedId);
  };

  // Stable across renders that don't change the feed list. The table calls this once per marker
  // cell, so a fresh closure every page render is a new prop identity on every row — the thing that
  // stops the row from ever being memoizable.
  const isDrillable = React.useCallback((input) => canDrillDown(input, feeds), [feeds]);

  // And the same problem for the press itself, which handleDrillDown had. It closes over the form,
  // the column negotiation and the navigation reducer, so it cannot be useCallback'd without
  // stabilising all three — and it is rebuilt on every keystroke in the feed box.
  //
  // A row needs the handler to be CURRENT, not to be the same function, so the identity is pinned
  // and the behaviour read through a ref at press time. Without this, FeedTableRow's React.memo
  // never hits and typing a feed id re-renders every row of a thousand-row table.
  const drillDownRef = React.useRef(null);
  drillDownRef.current = handleDrillDown;
  const onDrillDown = React.useCallback((input) => drillDownRef.current(input), []);

  // A retry re-sends the run that failed, which after a failed drill-down is the MEMBER's read. This
  // used to have to repair a ref first: the page had rolled the displayed-feed ref back to the
  // parent when that read brought back nothing, so without the repair the member's rows landed
  // described as the parent's. A response that names its own feed needs no repair — whatever comes
  // back says what it is about — so this is now just the hook's retry.
  const handleRetry = retry;

  // Step back up the path. The parent's rows are restored rather than re-read: they cannot have
  // changed, and the read is expensive.
  const handleBreadcrumb = (depth) => {
    const entry = popTo(depth);
    if (!entry) return;
    // Stepping up the path is not a top-level read, so a submit that never landed must not get to
    // wipe the path out from under the rows this puts back.
    keepPath();
    restore(entry.result);
  };

  // A finished read should announce itself and be one Tab away, rather than leaving the analyst to
  // notice that something appeared below the form.
  const resultsRef = React.useRef(null);
  React.useEffect(() => {
    if (result && resultsRef.current) {
      resultsRef.current.focus();
    }
  }, [result]);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        {/* Always 95% of the window, with nothing capping it: this mode is a wide grid — a dozen
            member columns plus the enrichment ones — and a fixed max-width would put it in a
            horizontal scroller on the screens that have room to show it. */}
        <Page width={'95%'} maxWidth={'none'}>
          {/* The label is passed for correctness but is inert today: BackArrowButton keeps only
              onClick and left, and renders a div — so it reaches neither the DOM nor the keyboard.
              Shipping it that way was a deliberate call; fixing the shared component would make
              this line start working as written. */}
          <BackArrowButton onClick={handleBack} aria-label={hasActiveFeed ? BACK_CLEAR_LABEL : BACK_HOME_LABEL} />
          <Container top={48}>
            <Body1 bold id={HEADING_ID} tabIndex={-1}>
              Feed Viewer
            </Body1>
            <Container top={4}>
              <Body7 isLowContrast>Reads any feed straight through the scoring engine.</Body7>
            </Container>

            <Container top={16}>
              <RecentRuns
                runs={recentRuns}
                onSelect={handleSelectRecentRun}
                onRemove={removeRecentRun}
                onClear={clearRecentRuns}
                runningKey={runningRunKey}
              />
              <FeedViewFormPanel onSubmit={handleSubmit} isRunning={isRunning} />
            </Container>

            <FeedRunStatus
              isRunning={isRunning}
              hasResult={!!result}
              error={error}
              retryable={retryable}
              onRetry={handleRetry}
              onCancel={cancel}
            />

            {result && (
              <FeedResultsPanel
                result={result}
                displayedFeedId={displayedFeedId}
                isSuperseded={isSuperseded}
                canReadSpecs={!!tools && tools.includes(AI_TOOLS.SCORE_SPEC_ADMIN)}
                stack={stack}
                onNavigate={handleBreadcrumb}
                hiddenColumns={hiddenColumns}
                onToggleColumn={toggleColumn}
                columnsRefused={columnsRefused}
                refusedColumnNames={refusedColumnNames}
                onAskForColumnsAgain={handleAskForColumnsAgain}
                canLoadColumns={canLoadColumns}
                loadColumnsLabel={loadColumnsButtonLabel}
                onLoadColumns={handleLoadColumns}
                onDrillDown={onDrillDown}
                canDrillDown={isDrillable}
                resultsRef={resultsRef}
              />
            )}
          </Container>
        </Page>
      </Body>

      {unavailableDrillDown && (
        <DrillDownUnavailableModal
          memberKey={unavailableDrillDown.memberKey}
          memberLabel={unavailableDrillDown.memberLabel}
          reason={unavailableDrillDown.reason}
          candidates={unavailableDrillDown.candidates}
          onDismiss={() => navigation.dismissUnavailable()}
          onOpenAnyway={(memberKey) => {
            // The list said no; the server hasn't been asked yet. GET /feeds fails open and omits
            // derived feeds, so an absent id is not evidence the feed is absent. Send the raw alias
            // and let the 422 — or the rows — settle it.
            navigation.dismissUnavailable();
            openMemberFeed(memberKey);
          }}
          onOpenCandidate={(candidateFeedId) => {
            // Ambiguity is about which feed the ROW meant, and only the analyst can settle that. The
            // press is an ordinary descent by id — same door as a resolved marker — so the pick lands
            // in the breadcrumb and can be stepped back out of if it turns out to be the wrong twin.
            navigation.dismissUnavailable();
            openMemberFeed(candidateFeedId);
          }}
        />
      )}
    </PageWrapper>
  );
};

export default FeedView;
